import { PageContainer, ProCard, ProTable, type ProColumns } from '@ant-design/pro-components';
import { Col, Grid, Row, Select, Statistic } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

type ActivityRow = {
  date: string;
  dau: number;
  logins: number;
  logouts: number;
  activationUsed: number;
};

type OverviewResponse = {
  activity: {
    range: { days: number; startDate: string; endDate: string };
    today: ActivityRow;
    series: ActivityRow[];
  };
  activationCodes: {
    total: number;
    used: number;
    unused: number;
    exported: number;
    unexported: number;
    usedToday: number;
  };
};

type AuditLogRow = {
  id: number;
  action: string;
  actorType: 'teacher' | 'admin' | 'anonymous';
  actorId: number | null;
  username: string | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string | Date;
};

const ACTION_ENUM: Record<string, { text: string }> = {
  ACTIVE: { text: '活跃' },
  TEACHER_LOGIN: { text: '教师登录' },
  TEACHER_LOGOUT: { text: '教师退出登录' },
  TEACHER_REGISTER_REQUEST: { text: '教师注册请求' },
  TEACHER_REGISTER_SUCCESS: { text: '教师注册成功' },
  TEACHER_REGISTER_FAILED: { text: '教师注册失败' },
  ACTIVATION_CODE_USED: { text: '激活码使用' },
  ADMIN_LOGIN: { text: '管理员登录' },
  ADMIN_LOGOUT: { text: '管理员退出登录' },
  ACTIVATION_CODE_BATCH_CREATE: { text: '批量生成激活码' },
  ACTIVATION_CODE_EXPORT: { text: '导出激活码' },
  ACTIVATION_CODE_VOID: { text: '作废激活码' },
  ACTIVATION_CODE_MARK_EXPORTED: { text: '标记激活码导出' },
};

export default function AnalyticsDashboard() {
  const [days, setDays] = useState(7);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setLoading(true);
      void api
        .get('/admin/stats/overview', { params: { days } })
        .then((res) => {
          if (cancelled) return;
          setOverview(res.data as OverviewResponse);
        })
        .finally(() => {
          if (cancelled) return;
          setLoading(false);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [days]);

  // const activityColumns: ProColumns<ActivityRow>[] = [
  //   { title: '日期', dataIndex: 'date', search: false, width: 120 },
  //   { title: 'DAU', dataIndex: 'dau', search: false, width: 100 },
  //   { title: '登录次数', dataIndex: 'logins', search: false, width: 120 },
  //   { title: '退出次数', dataIndex: 'logouts', search: false, width: 120 },
  //   { title: '激活码使用', dataIndex: 'activationUsed', search: false, width: 140 },
  // ];

  const logColumns: ProColumns<AuditLogRow>[] = useMemo(
    () => [
      { title: '时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false, width: 180 },
      {
        title: '操作',
        dataIndex: 'action',
        valueEnum: ACTION_ENUM,
        width: 160,
      },
      {
        title: '操作者类型',
        dataIndex: 'actorType',
        valueEnum: {
          teacher: { text: '教师' },
          admin: { text: '管理员' },
          anonymous: { text: '匿名' },
        },
        width: 120,
      },
      { title: '用户名', dataIndex: 'username', width: 140 },
      { title: '操作者ID', dataIndex: 'actorId', search: false, width: 110 },
      // { title: 'IP', dataIndex: 'ip', search: false, width: 140 },
      // { title: 'UA', dataIndex: 'userAgent', search: false, ellipsis: true, width: 200 },
      {
        title: '详情',
        dataIndex: 'metadata',
        search: false,
        width: 260,
        // ellipsis: true,
        render: (_, record) => (record.metadata ? JSON.stringify(record.metadata) : '-'),
      },
      {
        title: '时间范围',
        dataIndex: 'createdAtRange',
        valueType: 'dateTimeRange',
        hideInTable: true,
      },
    ],
    [],
  );

  const today = overview?.activity.today;
  const activation = overview?.activationCodes;

  return (
    <PageContainer
      title="数据看板"
      extra={[
        <Select
          key="days"
          value={days}
          style={{ width: 140 }}
          options={[
            { label: '近 7 天', value: 7 },
            { label: '近 14 天', value: 14 },
            { label: '近 30 天', value: 30 },
          ]}
          onChange={setDays}
        />,
      ]}
    >
      <ProCard loading={loading} gutter={16} direction="column">
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <ProCard bordered title="今日概览" bodyStyle={{ padding: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Statistic title="今日 DAU" value={today?.dau ?? 0} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="今日登录" value={today?.logins ?? 0} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="今日退出" value={today?.logouts ?? 0} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="今日激活码使用" value={today?.activationUsed ?? 0} />
                </Col>
              </Row>
            </ProCard>
          </Col>
          <Col xs={24} xl={12}>
            <ProCard bordered title="激活码概览" bodyStyle={{ padding: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={8}>
                  <Statistic title="激活码总量" value={activation?.total ?? 0} />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic title="已使用" value={activation?.used ?? 0} />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic title="未使用" value={activation?.unused ?? 0} />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic title="已导出" value={activation?.exported ?? 0} />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic title="未导出" value={activation?.unexported ?? 0} />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic title="今日使用" value={activation?.usedToday ?? 0} />
                </Col>
              </Row>
            </ProCard>
          </Col>
        </Row>

        {/* <ProCard bordered title="近 N 天趋势" style={{ marginTop: '16px' }}>
          <ProTable<ActivityRow>
            columns={activityColumns}
            dataSource={overview?.activity.series ?? []}
            rowKey="date"
            search={false}
            options={false}
            pagination={false}
            scroll={{ x: 650 }}
          />
        </ProCard> */}

        <ProCard bordered title="关键操作日志" style={{ marginTop: '16px' }}>
          <ProTable<AuditLogRow>
            columns={logColumns}
            rowKey="id"
            options={false}
            tableLayout="fixed"
            pagination={{ pageSize: 20 }}
            scroll={{ x: 1300 }}
            search={isMobile ? { labelWidth: 'auto', span: 24, defaultCollapsed: true } : { labelWidth: 92 }}
            request={async (params: { current?: number; pageSize?: number; action?: string; actorType?: string; username?: string; createdAtRange?: [string, string] }) => {
              const [dateFrom, dateTo] = Array.isArray(params.createdAtRange) ? params.createdAtRange : [];
              const res = await api.get('/admin/logs', {
                params: {
                  page: params.current,
                  limit: params.pageSize,
                  action: params.action,
                  actorType: params.actorType,
                  username: params.username,
                  dateFrom,
                  dateTo,
                },
              });
              return {
                data: res.data.items,
                total: res.data.total,
                success: true,
              };
            }}
          />
        </ProCard>
      </ProCard>
    </PageContainer>
  );
}
